// src/utils/kitchenGeometry.js

const DEFAULT_WALL_HEIGHT_FT = 8;
const DEFAULT_WALL_THICKNESS_FT = 0.35;

/* =========================================================
   PUBLIC ENTRY
   ========================================================= */

export function buildKitchenGeometry({
  layout,
  walls,
  defaultHeightFt = DEFAULT_WALL_HEIGHT_FT,
}) {
  if (!layout || !walls) {
    return {
      walls: [],
      floor: null,
    };
  }

  if (layout === "l-shape") {
    return buildLShape(
      walls,
      defaultHeightFt
    );
  }

  if (layout === "u-shape") {
    return buildUShape(
      walls,
      defaultHeightFt
    );
  }

  return {
    walls: [],
    floor: null,
  };
}

/* =========================================================
   HEIGHT HELPERS
   ========================================================= */

/*
 * Kitchen Mode already allows the user to measure wall height.
 *
 * RULE:
 * If a valid measured height exists, it is authoritative.
 * The 8 ft default is used only when no height was measured.
 */

function resolveWallHeight(
  wall,
  defaultHeightFt
) {
  const measuredHeight =
    Number(wall?.heightFt);

  const hasMeasuredHeight =
    Number.isFinite(measuredHeight) &&
    measuredHeight > 0;

  return {
    heightFt: hasMeasuredHeight
      ? measuredHeight
      : defaultHeightFt,

    heightSource: hasMeasuredHeight
      ? "measured"
      : "assumed",
  };
}

/* =========================================================
   L-SHAPE
   ========================================================= */

function buildLShape(
  walls,
  defaultHeightFt
) {
  const backLength =
    Number(
      walls.back?.lengthFt || 0
    );

  const sideLength =
    Number(
      walls.side?.lengthFt || 0
    );

  if (
    !backLength ||
    !sideLength
  ) {
    return {
      walls: [],
      floor: null,
    };
  }

  const backHeight =
    resolveWallHeight(
      walls.back,
      defaultHeightFt
    );

  const sideHeight =
    resolveWallHeight(
      walls.side,
      defaultHeightFt
    );

  const thickness =
    DEFAULT_WALL_THICKNESS_FT;

  return {
    layout: "l-shape",

    walls: [
      {
        id: "back",
        label: "Back wall",

        lengthFt: backLength,

        heightFt:
          backHeight.heightFt,

        heightSource:
          backHeight.heightSource,

        thicknessFt:
          thickness,

        /*
         * Back wall extends
         * left-to-right along X.
         */
        position: [
          backLength / 2,
          backHeight.heightFt / 2,
          0,
        ],

        rotation: [
          0,
          0,
          0,
        ],
      },

      {
        id: "side",
        label: "Side wall",

        lengthFt: sideLength,

        heightFt:
          sideHeight.heightFt,

        heightSource:
          sideHeight.heightSource,

        thicknessFt:
          thickness,

        /*
         * Side wall extends toward
         * the viewer along Z.
         */
        position: [
          0,
          sideHeight.heightFt / 2,
          sideLength / 2,
        ],

        rotation: [
          0,
          Math.PI / 2,
          0,
        ],
      },
    ],

    floor: {
      widthFt:
        backLength,

      depthFt:
        sideLength,

      position: [
        backLength / 2,
        0,
        sideLength / 2,
      ],
    },
  };
}

/* =========================================================
   U-SHAPE
   ========================================================= */

function buildUShape(
  walls,
  defaultHeightFt
) {
  const leftLength =
    Number(
      walls.left?.lengthFt || 0
    );

  const backLength =
    Number(
      walls.back?.lengthFt || 0
    );

  const rightLength =
    Number(
      walls.right?.lengthFt || 0
    );

  if (
    !leftLength ||
    !backLength ||
    !rightLength
  ) {
    return {
      walls: [],
      floor: null,
    };
  }

  const backHeight =
    resolveWallHeight(
      walls.back,
      defaultHeightFt
    );

  const leftHeight =
    resolveWallHeight(
      walls.left,
      defaultHeightFt
    );

  const rightHeight =
    resolveWallHeight(
      walls.right,
      defaultHeightFt
    );

  const thickness =
    DEFAULT_WALL_THICKNESS_FT;

  const depth =
    Math.max(
      leftLength,
      rightLength
    );

  return {
    layout: "u-shape",

    walls: [
      {
        id: "back",
        label: "Back wall",

        lengthFt:
          backLength,

        heightFt:
          backHeight.heightFt,

        heightSource:
          backHeight.heightSource,

        thicknessFt:
          thickness,

        position: [
          backLength / 2,
          backHeight.heightFt / 2,
          0,
        ],

        rotation: [
          0,
          0,
          0,
        ],
      },

      {
        id: "left",
        label: "Left wall",

        lengthFt:
          leftLength,

        heightFt:
          leftHeight.heightFt,

        heightSource:
          leftHeight.heightSource,

        thicknessFt:
          thickness,

        position: [
          0,
          leftHeight.heightFt / 2,
          leftLength / 2,
        ],

        rotation: [
          0,
          Math.PI / 2,
          0,
        ],
      },

      {
        id: "right",
        label: "Right wall",

        lengthFt:
          rightLength,

        heightFt:
          rightHeight.heightFt,

        heightSource:
          rightHeight.heightSource,

        thicknessFt:
          thickness,

        position: [
          backLength,
          rightHeight.heightFt / 2,
          rightLength / 2,
        ],

        rotation: [
          0,
          Math.PI / 2,
          0,
        ],
      },
    ],

    floor: {
      widthFt:
        backLength,

      depthFt:
        depth,

      position: [
        backLength / 2,
        0,
        depth / 2,
      ],
    },
  };
}
